import { adminApi } from './adminApi'

/**
 * Monitoring Service
 *
 * API service layer for the monitoring dashboard.
 * Uses adminApi (not regular api) to see data across ALL organizations.
 *
 * ⚠️ SECURITY: This service is for ADMIN-ONLY endpoints.
 * All methods call endpoints that require admin role validation on backend.
 *
 * @see docs/MONITORING_DASHBOARD_ARCHITECTURE.md Section 4.1 and 5.5
 */

// ============================================================================
// Type Definitions
// ============================================================================

export interface JobStatus {
  job_id: number
  integration_id: number
  integration_name: string
  organization_id: number
  organization_name: string
  current_step: 'collect' | 'load' | 'push'
  status: 'running' | 'queued' | 'success' | 'failed'
  progress_pct: number
  records_processed: number
  eta_seconds: number
  started_at: string
  periods_completed?: number
  periods_running?: number
  total_periods?: number
  periods_failed?: number
  queue_position?: number
  period_start?: string | null
  period_end?: string | null
}

export interface DailySummary {
  date: string
  org_id: number | null
  summary: {
    total_jobs: number
    successful_jobs: number
    failed_jobs: number
    active_jobs: number
    success_rate: number
    integrations_run: number
    organizations_affected: number
  }
  periods: {
    total: number
    successful: number
    failed: number
    success_rate: number
  }
  data: {
    total_records: number
    avg_period_duration_seconds: number
  }
  top_failures: FailureInfo[]
}

export interface FailureInfo {
  integration_id: number
  integration_name: string
  organization_name: string
  failed_jobs: number
  failed_periods: number
  common_error: string
}

export interface TrendData {
  date: string
  total_jobs: number
  successful_jobs: number
  failed_jobs: number
  success_rate: number
  total_records: number
  avg_period_duration_seconds: number
}

export interface TrendsResponse {
  start_date: string
  end_date: string
  days: number
  org_id: number | null
  trends: TrendData[]
  summary: {
    total_jobs: number
    successful_jobs: number
    failed_jobs: number
    overall_success_rate: number
  }
}

export interface FailureGroup {
  integration_id: number
  integration_name: string
  organization_name: string
  total_failed: number
  retriable_count: number
  exhausted_count: number
  error_categories: Array<{
    category: string
    count: number
  }>
}

export interface FailurePeriod {
  period_id: number
  job_period: string
  step: string
  error_category: string
  error_message: string
  retry_count: number
  max_retries: number
}

export interface OrganizationMetrics {
  integration_id: number
  integration_name: string
  table_name: string
  metrics: Array<{
    date: string
    record_count: number
    ad_revenue?: number
    ua_spent?: number
    iap_revenue?: number
  }>
}

export interface WorkerStatus {
  status: 'healthy' | 'degraded' | 'critical'
  workers: Array<{
    name: string
    status: 'active' | 'inactive'
    active_tasks: number
    queue: string
  }>
  queues: Array<{
    name: string
    depth: number
    consumers: number
  }>
  components: {
    postgres: boolean
    clickhouse: boolean
    redis: boolean
    celery: boolean
  }
  metrics: {
    avg_collect_duration: number
    avg_load_duration: number
    avg_push_duration: number
    success_rate_24h: number
  }
  timestamp: string
}

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'critical'
  components: {
    postgres: boolean
    clickhouse: boolean
    redis: boolean
    celery: boolean
  }
  metrics: {
    avg_collect_duration: number
    avg_load_duration: number
    avg_push_duration: number
    success_rate_24h: number
  }
}

export interface JobsPerformanceBatch {
  batch: string
  batch_timestamp: string | null
  hourly: number
  daily: number
  reprocess: number
  backfill: number
  email: number
  push: number
  total_jobs: number
  completed: number
  in_progress: number
  pending: number
  errors: number
  total_records: number
  total_min: number
}

export interface JobsPerformanceResponse {
  start_date: string
  end_date: string
  batches: JobsPerformanceBatch[]
  total_batches: number
}

export interface LegacyGatewayHealth {
  status: 'healthy' | 'warning' | 'critical'
  failed_1h: number
  failed_24h: number
  stuck_jobs: number
  completed_1h: number
  completed_24h: number
  total_24h: number
}

export interface JobSearchFilters {
  start_date?: string
  end_date?: string
  kind?: string  // Single: 'hourly', 'daily', 'reprocess', 'backfill', 'email'
  status?: string  // Single or comma-separated: 'queued', 'running', 'completed', 'failed'
  has_errors?: boolean
  is_actually_running?: boolean  // Jobs with status=running AND at least one period running
  is_pending_dispatch?: boolean  // Jobs with status=running but no periods running yet
  integration_id?: number
  org_id?: number
  org_ids?: string  // Comma-separated org IDs for multi-select filter
  search?: string  // Search in integration ID, integration name
  org_name?: string  // Filter by organization name (partial match)
  limit?: number
  offset?: number
}

export interface JobSearchResult {
  job_id: number
  integration_id: number
  integration_name: string
  organization_id: number
  organization_name: string
  kind: string
  behavior_category: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  current_step: string | null
  progress_pct: number
  total_periods: number
  periods_completed: number
  periods_running: number
  periods_failed: number
  records_processed: number
  created_at: string | null
  updated_at: string | null
  started_at: string | null
  completed_at: string | null
  error_category: string | null
  error_message: string | null
  period_start: string | null
  period_end: string | null
}

export interface JobsFilterOptions {
  organizations: Array<{ id: number; name: string }>
}

export interface JobSearchResponse {
  jobs: JobSearchResult[]
  total: number
  limit: number
  offset: number
  filters: JobSearchFilters
  filter_options: JobsFilterOptions
}

// ============================================================================
// Monitoring Service Class
// ============================================================================

class MonitoringService {
  private baseUrl = '/cube/admin/cube'

  /**
   * Get list of currently active (running) collection jobs
   * @param filters Optional filters for org, status, integration
   */
  async getActiveJobs(filters?: {
    org_id?: number
    status?: string
    integration_id?: number
  }): Promise<{
    jobs: JobStatus[]
    total_queued: number
    total_running: number
    periodic_queued: number
    backfill_queued: number
    total_errors: number
    avg_period_duration: number
  }> {
    const response = await adminApi.get(`${this.baseUrl}/jobs/active`, {
      params: filters
    })
    return {
      jobs: response.data.jobs || [],
      total_queued: response.data.total_queued || 0,
      total_running: response.data.total_running || 0,
      periodic_queued: response.data.periodic_queued || 0,
      backfill_queued: response.data.backfill_queued || 0,
      total_errors: response.data.total_errors || 0,
      avg_period_duration: response.data.avg_period_duration || 0
    }
  }

  /**
   * Get detailed progress for a specific job
   * @param jobId The job ID to get progress for
   */
  async getJobProgress(jobId: number): Promise<{
    job_id: number
    step: string
    records_processed: number
    total_records: number
    duration_seconds: number
    eta_seconds: number
  }> {
    const response = await adminApi.get(`${this.baseUrl}/jobs/${jobId}/progress`)
    return response.data
  }

  /**
   * Get daily summary of pipeline health
   * @param date Optional date (defaults to yesterday)
   * @param orgId Optional organization filter
   */
  async getDailySummary(date?: string, orgId?: number): Promise<DailySummary> {
    const params: any = {}
    if (date) params.date = date
    if (orgId) params.org_id = orgId

    const response = await adminApi.get(`${this.baseUrl}/dashboard/daily-summary`, { params })
    return response.data
  }

  /**
   * Get trend data for last N days
   * @param days Number of days (7 or 30)
   * @param orgId Optional organization filter
   */
  async getTrends(days: number = 7, orgId?: number): Promise<TrendsResponse> {
    const params: any = { days }
    if (orgId) params.org_id = orgId

    const response = await adminApi.get(`${this.baseUrl}/dashboard/trends`, { params })
    return response.data
  }

  /**
   * Get grouped failures (for smart visualization)
   * @param filters Date range, grouping strategy, retriable filter
   */
  async getFailuresGrouped(filters: {
    dateFrom?: string
    dateTo?: string
    groupBy?: 'integration' | 'organization' | 'error_category'
    onlyRetriable?: boolean
  }): Promise<FailureGroup[]> {
    const response = await adminApi.get(`${this.baseUrl}/failures/grouped`, {
      params: {
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        group_by: filters.groupBy || 'integration',
        only_retriable: filters.onlyRetriable ?? true
      }
    })
    return response.data.groups || []
  }

  /**
   * Get individual failed periods for a specific integration
   * @param filters Integration and date range
   */
  async getFailurePeriods(filters: {
    integrationId: number
    dateFrom?: string
    dateTo?: string
  }): Promise<FailurePeriod[]> {
    const response = await adminApi.get(`${this.baseUrl}/failures/periods`, {
      params: {
        integration_id: filters.integrationId,
        date_from: filters.dateFrom,
        date_to: filters.dateTo
      }
    })
    return response.data.periods || []
  }

  /**
   * Retry a single failed period
   * @param periodId The period ID to retry
   */
  async retryPeriod(periodId: number): Promise<void> {
    await adminApi.post(`${this.baseUrl}/retry/period/${periodId}/reset`)
  }

  /**
   * Retry multiple failed periods at once
   * @param periodIds Array of period IDs to retry
   */
  async retryBulk(periodIds: number[]): Promise<{
    retry_count: number
    queued_jobs: number[]
  }> {
    const response = await adminApi.post(`${this.baseUrl}/retry/bulk`, {
      period_ids: periodIds
    })
    return response.data
  }

  /**
   * Trigger backfill for integrations over date range
   * @param integrationIds Array of integration IDs
   * @param dateFrom Start date (YYYY-MM-DD)
   * @param dateTo End date (YYYY-MM-DD)
   */
  async backfill(
    integrationIds: number[],
    dateFrom: string,
    dateTo: string
  ): Promise<{
    backfill_job_ids: number[]
    estimated_duration: number
  }> {
    const response = await adminApi.post(`${this.baseUrl}/backfill`, {
      integration_ids: integrationIds,
      date_from: dateFrom,
      date_to: dateTo
    })
    return response.data
  }

  /**
   * Get cube data metrics for an organization
   * @param orgId Organization ID
   * @param filters Optional integration and date filters
   */
  async getOrganizationData(
    orgId: number,
    filters?: {
      integrationId?: number
      dateFrom?: string
      dateTo?: string
    }
  ): Promise<OrganizationMetrics[]> {
    const response = await adminApi.get(`${this.baseUrl}/organizations/${orgId}/data`, {
      params: {
        integration_id: filters?.integrationId,
        date_from: filters?.dateFrom,
        date_to: filters?.dateTo
      }
    })
    return response.data.integrations || []
  }

  /**
   * Get Celery worker and queue status
   */
  async getWorkersStatus(): Promise<WorkerStatus> {
    const response = await adminApi.get(`${this.baseUrl}/workers/status`)
    return response.data
  }

  /**
   * Get overall system health status
   */
  async getSystemHealth(): Promise<SystemHealth> {
    const response = await adminApi.get(`${this.baseUrl}/health/system`)
    return response.data
  }

  /**
   * Get existing retry statistics (uses existing endpoint)
   */
  async getRetryStats(): Promise<any> {
    const response = await adminApi.get(`${this.baseUrl}/retry/stats`)
    return response.data
  }

  /**
   * Get currently active locks (uses existing endpoint)
   */
  async getActiveLocks(): Promise<any> {
    const response = await adminApi.get(`${this.baseUrl}/locks/active`)
    return response.data
  }

  /**
   * Get jobs performance aggregated by hour
   * @param hours Number of hours to look back (default: 24)
   * @param startDate Optional start date for historical analysis
   * @param endDate Optional end date for historical analysis
   * @param organizationId Optional organization ID to filter by
   */
  async getJobsPerformanceByHour(
    hours: number = 24,
    startDate?: string,
    endDate?: string,
    organizationId?: number
  ): Promise<JobsPerformanceResponse> {
    const params: any = { hours }
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (organizationId) params.organization_id = organizationId

    // Direct DB access (bypasses cube proxy)
    const response = await adminApi.get(`${this.baseUrl}/health/jobs/performance-by-hour`, { params })
    return response.data
  }

  /**
   * Search jobs with flexible filters
   * @param filters Search filters (date range, kind, status, has_errors, etc.)
   */
  async searchJobs(filters: JobSearchFilters): Promise<JobSearchResponse> {
    const params: any = {}

    if (filters.start_date) params.start_date = filters.start_date
    if (filters.end_date) params.end_date = filters.end_date
    if (filters.kind) params.kind = filters.kind
    if (filters.status) params.status = filters.status
    if (filters.has_errors !== undefined) params.has_errors = filters.has_errors
    if (filters.is_actually_running !== undefined) params.is_actually_running = filters.is_actually_running
    if (filters.is_pending_dispatch !== undefined) params.is_pending_dispatch = filters.is_pending_dispatch
    if (filters.integration_id) params.integration_id = filters.integration_id
    if (filters.org_id) params.org_id = filters.org_id
    if (filters.org_ids) params.org_ids = filters.org_ids
    if (filters.search) params.search = filters.search
    if (filters.org_name) params.org_name = filters.org_name
    if (filters.limit) params.limit = filters.limit
    if (filters.offset) params.offset = filters.offset

    // Direct DB access (bypasses cube proxy)
    const response = await adminApi.get(`${this.baseUrl}/health/jobs/search`, { params })
    return response.data
  }

  // ==========================================================================
  // Health & Alerts API Methods
  // ==========================================================================

  /**
   * Get overall health summary
   */
  async getHealthSummary(): Promise<HealthSummary> {
    const response = await adminApi.get(`${this.baseUrl}/health/summary`)
    return response.data
  }

  /**
   * Get credential errors
   */
  async getCredentialErrors(): Promise<CredentialErrorsResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/credential-errors`)
    return response.data
  }

  /**
   * Get exhausted periods
   */
  async getExhaustedPeriods(days: number = 7, limit: number = 50): Promise<ExhaustedPeriodsResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/exhausted-periods`, {
      params: { days, limit }
    })
    return response.data
  }

  /**
   * Get degraded integrations
   */
  async getDegradedIntegrations(days: number = 7, threshold: number = 0.7): Promise<DegradedIntegrationsResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/degraded-integrations`, {
      params: { days, threshold }
    })
    return response.data
  }

  /**
   * Get error patterns
   */
  async getErrorPatterns(hours: number = 24): Promise<ErrorPatternsResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/error-patterns`, {
      params: { hours }
    })
    return response.data
  }

  /**
   * Get retrying periods
   */
  async getRetryingPeriods(limit: number = 50): Promise<RetryingPeriodsResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/retrying-periods`, {
      params: { limit }
    })
    return response.data
  }

  /**
   * Get recovered today stats
   */
  async getRecoveredToday(): Promise<RecoveredTodayResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/recovered-today`)
    return response.data
  }

  /**
   * Get data gaps with pagination
   */
  async getDataGaps(status?: string, severity?: string, page: number = 1, size: number = 50): Promise<DataGapsResponse> {
    const params: any = { page, size }
    if (status) params.status = status
    if (severity) params.severity = severity

    const response = await adminApi.get(`${this.baseUrl}/health/data-gaps`, { params })
    return response.data
  }

  /**
   * Trigger backfill for data gaps
   */
  async triggerGapBackfill(gapIds: number[], triggeredBy: string = 'admin'): Promise<BackfillResult> {
    const response = await adminApi.post(`${this.baseUrl}/health/data-gaps/backfill`, {
      gap_ids: gapIds,
      triggered_by: triggeredBy
    })
    return response.data
  }

  /**
   * Trigger data gap check for specific integrations
   * Fast path for checking gaps after backfill completes
   */
  async checkIntegrationDataGaps(integrationIds: number[]): Promise<{ status: string; task_id: string; integration_ids: number[]; message: string }> {
    const response = await adminApi.post(`${this.baseUrl}/health/data-gaps/check`, null, {
      params: { integration_ids: integrationIds }
    })
    return response.data
  }

  /**
   * Acknowledge a data gap
   */
  async acknowledgeDataGap(gapId: number, acknowledgedBy: string = 'admin'): Promise<void> {
    await adminApi.post(`${this.baseUrl}/health/data-gaps/${gapId}/acknowledge`, null, {
      params: { acknowledged_by: acknowledgedBy }
    })
  }

  /**
   * Retry all failed/exhausted periods for a job
   */
  async retryFailedJob(jobId: number): Promise<RetryJobResult> {
    const response = await adminApi.post(`${this.baseUrl}/jobs/${jobId}/retry-failed`)
    return response.data
  }

  /**
   * Retry multiple failed periods in bulk
   */
  async retryFailedPeriodsBulk(periodIds: number[]): Promise<BulkRetryResult> {
    const response = await adminApi.post(`${this.baseUrl}/jobs/retry-failed-bulk`, {
      period_ids: periodIds
    })
    return response.data
  }

  /**
   * Get errors (failed/exhausted job periods) with pagination, sorting, and filtering
   */
  async getErrors(filters: ErrorsFilters = {}): Promise<ErrorsResponse> {
    const params: any = {}

    if (filters.hours) params.hours = filters.hours
    if (filters.page) params.page = filters.page
    if (filters.size) params.size = filters.size
    if (filters.sort_by) params.sort_by = filters.sort_by
    if (filters.sort_dir) params.sort_dir = filters.sort_dir
    if (filters.organization_ids?.length) params.organization_ids = filters.organization_ids.join(',')
    if (filters.integration_ids?.length) params.integration_ids = filters.integration_ids.join(',')
    if (filters.integrator_slugs?.length) params.integrator_slugs = filters.integrator_slugs.join(',')
    if (filters.categories?.length) params.categories = filters.categories.join(',')
    if (filters.statuses?.length) params.statuses = filters.statuses.join(',')
    if (filters.batches?.length) params.batches = filters.batches.join(',')
    if (filters.period_statuses?.length) params.period_statuses = filters.period_statuses.join(',')

    const response = await adminApi.get(`${this.baseUrl}/health/errors`, { params })
    return response.data
  }

  /**
   * Get count of new/updated credentials since a specific date
   * @param sinceDate Date in YYYY-MM-DD format (UTC)
   */
  async getCredentialsUpdatedCount(sinceDate: string = '2026-01-06'): Promise<CredentialsUpdatedResponse> {
    const response = await adminApi.get(`${this.baseUrl}/health/credentials-updated`, {
      params: { since: sinceDate }
    })
    return response.data
  }

  /**
   * Download integration audit CSV
   * Triggers a file download of all enabled integrations with their job counts
   * from the last 24 hours, categorized by audit status.
   */
  async downloadAuditCsv(): Promise<void> {
    const response = await adminApi.get(`${this.baseUrl}/health/audit-csv`, {
      responseType: 'blob'
    })

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = 'integration_audit.csv'
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=([^;]+)/)
      if (match) {
        filename = match[1].replace(/["']/g, '')
      }
    }

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  /**
   * Get legacy gateway health status
   * Returns job statistics for the /v2/data API gateway
   */
  async getLegacyGatewayHealth(): Promise<LegacyGatewayHealth> {
    const response = await adminApi.get('/v2/data/health')
    return response.data
  }

  /**
   * Get records pivot table data
   * Returns records collected per integration per day for a date range
   * @param startDate Start date (YYYY-MM-DD). Defaults to 7 days ago.
   * @param endDate End date (YYYY-MM-DD). Defaults to today.
   * @param organizationId Filter by organization ID.
   */
  async getRecordsPivot(startDate?: string, endDate?: string, organizationId?: number): Promise<RecordsPivotResponse> {
    const params: Record<string, string | number> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (organizationId) params.organization_id = organizationId

    const response = await adminApi.get(`${this.baseUrl}/health/records-pivot`, { params })
    return response.data
  }
}

// =============================================================================
// Health & Alerts Type Definitions
// =============================================================================

export interface HealthSummary {
  critical_count: number
  action_count: number
  monitoring_count: number
  details: {
    credential_errors: number
    exhausted_periods: number
    degraded_integrations: number
    data_gaps: {
      total: number
      emergency: number
      critical: number
    }
    retrying_periods: number
  }
  timestamp: string
}

export interface CredentialError {
  integration_id: number
  integration_name: string
  integrator_slug: string
  organization_id: number
  organization_name: string
  organization_slug: string
  status: 'credential_error' | 'revoked' | 'unknown'
  blocked_since: string | null
  last_error: string | null
}

export interface CredentialErrorsResponse {
  errors: CredentialError[]
  total: number
}

export interface ExhaustedPeriod {
  period_id: number
  job_id: number
  job_period: string | null
  step: string
  attempt_count: number
  error_category: string | null
  error_message: string | null
  exhausted_at: string | null
  integration_id: number
  integration_name: string
  organization_id: number
  organization_name: string
}

export interface ExhaustedPeriodsResponse {
  periods: ExhaustedPeriod[]
  total: number
  days: number
}

export interface DegradedIntegration {
  integration_id: number
  integration_name: string
  organization_id: number
  organization_name: string
  integrator_slug: string
  total_periods: number
  completed_periods: number
  failed_periods: number
  exhausted_periods: number
  success_rate: number
  trend_direction: 'improving' | 'stable' | 'degrading' | 'unknown'
  recent_success_rate: number
  historical_success_rate: number
  success_rate_change: number
  severity: 'info' | 'warning' | 'critical' | 'emergency'
  is_degraded: boolean
  consecutive_failures: number
  last_failure_date: string | null
}

export interface DegradedIntegrationsResponse {
  integrations: DegradedIntegration[]
  total: number
  threshold: number
  days: number
}

export interface ErrorPattern {
  error_category: string
  error_pattern: string
  action_type: 'actionable' | 'informational'
  occurrence_count: number
  affected_integrations: number
  affected_organizations: number
  last_seen: string | null
  integration_ids: number[]
  integration_names: string[]
  integrator_slugs: string[]
}

export interface ErrorPatternsResponse {
  patterns: ErrorPattern[]
  total: number
  hours: number
}

export interface RetryingPeriod {
  period_id: number
  job_id: number
  job_period: string | null
  step: string
  attempt_count: number
  error_category: string | null
  error_message: string | null
  integration_id: number
  integration_name: string
  organization_name: string
}

export interface RetryingPeriodsResponse {
  periods: RetryingPeriod[]
  total: number
}

export interface RecoveredTodayResponse {
  total_recovered: number
  integrations_recovered: number
  avg_attempts_to_recover: number
  by_step: Record<string, number>
  date: string
}

export interface DataGap {
  id: number
  integration_id: number
  integration_name: string | null
  integration_status: string | null
  organization_id: number
  organization_name: string | null
  gap_date: string | null
  detected_at: string | null
  last_error_category: string | null
  last_error_message: string | null
  attempt_count: number
  status: string
  resolved_at: string | null
  resolved_by: string | null
  severity: 'low' | 'warning' | 'critical' | 'emergency'
}

export interface DataGapsResponse {
  gaps: DataGap[]
  total: number
  filters: {
    status: string | null
    severity: string | null
  }
}

export interface BackfillResult {
  requested: number
  scheduled: number
  failed: number
  job_ids: number[]
}

export interface RetryJobResult {
  success: boolean
  job_id: number
  periods_reset: number
  period_ids: number[]
}

export interface BulkRetryResult {
  success: boolean
  requested: number
  reset: number
  periods: Array<{ period_id: number; job_id: number }>
  jobs_affected: number
}

export interface CredentialsUpdatedResponse {
  count: number
  since_date: string
  breakdown: {
    new: number
    updated: number
  }
}

// =============================================================================
// Errors Table Types
// =============================================================================

export interface ErrorRecord {
  integration_id: number
  integration_name: string | null
  integrator_slug: string | null
  behavior_category: string | null  // hourly, daily, delayed, learning
  organization_id: number
  organization_name: string | null
  integration_status: number
  batch_timestamp: string | null
  error_message: string | null
  error_category: string | null
  // Period status counts
  queued_periods: number
  running_periods: number
  completed_periods: number
  failed_periods: number
  exhausted_periods: number
  total_periods: number
  gap_dates: string[]
  records: number
  elapsed_seconds: number | null
  // Hidden fields for backfill action
  job_id: number
  job_status: number | null  // 0=queued, 1=running, 2=completed, 3=failed
  job_kind: string | null
  job_target_from: string | null
  job_target_to: string | null
}

export interface ErrorsFilterOptions {
  organizations: Array<{ id: number; name: string }>
  integrators: string[]
  categories: string[]
  statuses: number[]
  batches: string[]
}

export interface ErrorsResponse {
  errors: ErrorRecord[]
  total: number
  page: number
  size: number
  filter_options: ErrorsFilterOptions
}

export interface ErrorsFilters {
  hours?: number
  page?: number
  size?: number
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
  organization_ids?: number[]
  integration_ids?: number[]
  integrator_slugs?: string[]
  categories?: string[]
  statuses?: number[]
  batches?: string[]
  period_statuses?: number[]  // 0=queued, 1=running, 2=completed, 3=failed, 6=exhausted
}

// =============================================================================
// Records Pivot Table Types
// =============================================================================

export interface IntegrationDailyRecords {
  integration_id: number
  integration_name: string
  organization_id: number
  organization_name: string
  behavior_category: string | null  // hourly, daily, delayed, learning
  segment: string | null  // email, bucket, push, etc.
  daily_records: Record<string, number>  // date string -> records count
}

export interface RecordsPivotResponse {
  integrations: IntegrationDailyRecords[]
  dates: string[]  // List of date strings (oldest to newest)
  total_integrations: number
}

// =============================================================================
// Periodic Collection Control Types & Methods
// =============================================================================

export interface PeriodicCollectionStatus {
  paused: boolean
  paused_at: string | null
  paused_by: string | null
  reason: string | null
}

export interface PausePeriodicRequest {
  reason?: string
  paused_by?: string
}

// Export singleton instance
export const monitoringService = new MonitoringService()

// Periodic Collection Control API (separate from main monitoring service)
// Uses /cube/admin/orchestration endpoints
export const periodicCollectionApi = {
  /**
   * Get the current status of periodic collection
   */
  async getStatus(): Promise<PeriodicCollectionStatus> {
    const response = await adminApi.get('/cube/admin/orchestration/periodic-collection/status')
    return response.data
  },

  /**
   * Pause periodic collection
   */
  async pause(request: PausePeriodicRequest = {}): Promise<PeriodicCollectionStatus> {
    const response = await adminApi.post('/cube/admin/orchestration/periodic-collection/pause', {
      reason: request.reason || 'Manual pause from monitoring UI',
      paused_by: request.paused_by || 'admin'
    })
    return response.data
  },

  /**
   * Resume periodic collection
   */
  async resume(): Promise<PeriodicCollectionStatus> {
    const response = await adminApi.post('/cube/admin/orchestration/periodic-collection/resume')
    return response.data
  }
}

// =============================================================================
// Behavior Learning Types & API
// =============================================================================

// Legacy categories (old system)
export type BehaviorCategory = 'learning' | 'hourly' | 'daily' | 'delayed' | 'd1_only' | 'async'
// Simplified classification (new system)
export type SimplifiedClassification = 'hourly' | 'daily' | 'not_yet_classified' | 'async'
export type ReprocessStatus = 'learning' | 'optimized'

export interface BehaviorProfile {
  id: number
  integration_id: number
  integration_name: string
  integrator_slug: string
  organization_name: string
  // Simplified scheduler fields (new)
  classification: SimplifiedClassification  // Current classification from simplified scheduler
  is_async: boolean  // True for email/bucket/push segments
  segment?: string | null  // Integrator segment (email, bucket, push, collection, etc.)
  // Legacy fields (old system, kept for backwards compatibility)
  behavior_category: BehaviorCategory | null
  forced_category: BehaviorCategory | null
  optimal_collection_hour: number | null
  days_analyzed: number | null
  pattern_confidence: number | null
  anomaly_count: number
  learning_started_at: string | null
  learning_completed_at: string | null
  last_analyzed_at: string | null
  // Rate limit tracking (deprecated - not actively used)
  hourly_api_limit: number | null
  hourly_calls: number
  hourly_reset_at: string | null
  daily_api_limit: number | null
  daily_calls: number
  daily_reset_at: string | null
  last_rate_limit_hit_at: string | null
  rate_limit_type: 'hourly' | 'daily' | null
  is_rate_limited: boolean
  // Fingerprint learning fields (deprecated - removed in simplified scheduler)
  availability_lag_days: number | null
  availability_confidence: number | null
  fluctuation_days: number | null
  fluctuation_confidence: number | null
  max_observed_change_lag: number | null
  last_fingerprint_at: string | null
  consecutive_stable_days: number | null
  computed_reprocess_periods: number[]
  reprocess_status: ReprocessStatus | null
}

export interface BehaviorProfileSummary {
  // Simplified scheduler counts (new - primary)
  hourly: number
  daily: number
  not_yet_classified: number
  async_segments: number
  total: number
  simplified_scheduler_enabled: boolean
  // Legacy counts (old system - kept for backwards compatibility)
  learning: number
  delayed: number
  d1_only: number  // Legacy, same as delayed
  rate_limited: number
  overridden: number
  // Fingerprint learning counts (deprecated)
  reprocess_learning?: number
  reprocess_optimized?: number
  late_availability?: number
  stale_fingerprints?: number
}

export interface BehaviorProfilesFilters {
  // Simplified scheduler filter (new)
  classification?: SimplifiedClassification
  // Legacy filter (old system)
  category?: BehaviorCategory
  rate_limited?: boolean
  has_override?: boolean
  integrator?: string
  search?: string
  page?: number
  per_page?: number
  // Fingerprint learning filters (deprecated)
  reprocess_status?: ReprocessStatus
  availability?: 'same_day' | 'delayed' | 'very_delayed'
  has_recent_fingerprint?: boolean
  // Organization filter
  organization_id?: number
}

// Fingerprint history types
export interface FingerprintCollection {
  lag: number
  record_count: number
  changed: boolean
  collected_at: string
}

export interface FingerprintDay {
  query_date: string
  collections: FingerprintCollection[]
}

export interface FingerprintsResponse {
  integration_id: number
  fingerprints: FingerprintDay[]
  summary: {
    total_fingerprints: number
    dates_with_changes: number
    typical_availability_lag: number
    typical_finalization_lag: number
  }
}

export interface ResetFingerprintsResponse {
  success: boolean
  profile_id: number
  integration_id: number
  fingerprints_deleted: number
  message: string
}

export interface BehaviorProfilesResponse {
  profiles: BehaviorProfile[]
  total: number
  page: number
  per_page: number
}

export interface BehaviorLearningStatus {
  enabled: boolean
  disabled_at: string | null
  disabled_by: string | null
}

// Behavior Learning API
export const behaviorLearningApi = {
  /**
   * Get behavior profiles with filters and pagination
   */
  async getProfiles(filters: BehaviorProfilesFilters = {}): Promise<BehaviorProfilesResponse> {
    const params: Record<string, unknown> = {}

    // Simplified scheduler filter (new)
    if (filters.classification) params.classification = filters.classification
    // Legacy filter (old system)
    if (filters.category) params.category = filters.category
    if (filters.rate_limited !== undefined) params.rate_limited = filters.rate_limited
    if (filters.has_override !== undefined) params.has_override = filters.has_override
    if (filters.integrator) params.integrator = filters.integrator
    if (filters.search) params.search = filters.search
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.per_page = filters.per_page
    // Fingerprint learning filters (deprecated)
    if (filters.reprocess_status) params.reprocess_status = filters.reprocess_status
    if (filters.availability) params.availability = filters.availability
    if (filters.has_recent_fingerprint !== undefined) params.has_recent_fingerprint = filters.has_recent_fingerprint
    // Organization filter
    if (filters.organization_id) params.organization_id = filters.organization_id

    const response = await adminApi.get('/cube/admin/behavior-profiles', { params })
    return response.data
  },

  /**
   * Get summary counts by category
   */
  async getSummary(organizationId?: number): Promise<BehaviorProfileSummary> {
    const params: Record<string, unknown> = {}
    if (organizationId) params.organization_id = organizationId
    const response = await adminApi.get('/cube/admin/behavior-profiles/summary', { params })
    return response.data
  },

  /**
   * Set forced category override for an integration
   */
  async setOverride(profileId: number, forcedCategory: BehaviorCategory): Promise<BehaviorProfile> {
    const response = await adminApi.put(`/cube/admin/behavior-profiles/${profileId}/override`, {
      forced_category: forcedCategory
    })
    return response.data
  },

  /**
   * Clear forced category override
   */
  async clearOverride(profileId: number): Promise<BehaviorProfile> {
    const response = await adminApi.delete(`/cube/admin/behavior-profiles/${profileId}/override`)
    return response.data
  },

  /**
   * Get kill switch status
   */
  async getStatus(): Promise<BehaviorLearningStatus> {
    const response = await adminApi.get('/cube/admin/orchestration/behavior-learning/status')
    return response.data
  },

  /**
   * Enable behavior learning (clear kill switch)
   */
  async enable(): Promise<BehaviorLearningStatus> {
    const response = await adminApi.post('/cube/admin/orchestration/behavior-learning/enable')
    return response.data
  },

  /**
   * Disable behavior learning (set kill switch)
   */
  async disable(reason?: string): Promise<BehaviorLearningStatus> {
    const response = await adminApi.post('/cube/admin/orchestration/behavior-learning/disable', {
      reason: reason || 'Manual disable from monitoring UI',
      disabled_by: 'admin'
    })
    return response.data
  },

  /**
   * Get list of integrators that have behavior profiles (for filter dropdown)
   */
  async getIntegrators(): Promise<{ slug: string; name: string }[]> {
    const response = await adminApi.get('/cube/admin/behavior-profiles/integrators')
    return response.data.integrators || []
  },

  /**
   * Get fingerprint history for a profile
   * @param profileId The behavior profile ID
   * @param days Number of days of history (1-30, default 7)
   */
  async getFingerprints(profileId: number, days: number = 7): Promise<FingerprintsResponse> {
    const response = await adminApi.get(`/cube/admin/behavior-profiles/${profileId}/fingerprints`, {
      params: { days }
    })
    return response.data
  },

  /**
   * Reset fingerprint learning for a profile (admin only)
   * Deletes all fingerprints and resets learning fields to defaults
   * @param profileId The behavior profile ID
   */
  async resetFingerprints(profileId: number): Promise<ResetFingerprintsResponse> {
    const response = await adminApi.delete(`/cube/admin/behavior-profiles/${profileId}/fingerprints`)
    return response.data
  }
}

// =============================================================================
// Email Collection Control Types & API
// =============================================================================

export interface EmailCollectionStatus {
  paused: boolean
  paused_at: string | null
  paused_by: string | null
  reason: string | null
  email_integrations_count: number | null
}

export interface PauseEmailRequest {
  reason?: string
  paused_by?: string
}

// Email Collection Control API
// Uses /cube/admin/orchestration endpoints
export const emailCollectionApi = {
  /**
   * Get the current status of email collection
   */
  async getStatus(): Promise<EmailCollectionStatus> {
    const response = await adminApi.get('/cube/admin/orchestration/email-collection/status')
    return response.data
  },

  /**
   * Pause email collection
   */
  async pause(request: PauseEmailRequest = {}): Promise<EmailCollectionStatus> {
    const response = await adminApi.post('/cube/admin/orchestration/email-collection/pause', {
      reason: request.reason || 'Manual pause from monitoring UI',
      paused_by: request.paused_by || 'admin'
    })
    return response.data
  },

  /**
   * Resume email collection
   */
  async resume(): Promise<EmailCollectionStatus> {
    const response = await adminApi.post('/cube/admin/orchestration/email-collection/resume')
    return response.data
  },

  /**
   * Trigger email collection for a specific integration
   */
  async triggerCollection(integrationId: number): Promise<{ status: string; message: string; result: any }> {
    const response = await adminApi.post(`/cube/api/v1/collections/trigger-email/${integrationId}`)
    return response.data
  }
}
